import { A, G } from '@mobily/ts-belt';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import WarningIcon from '@mui/icons-material/Warning';
import { Box, Collapse, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRow,
  GridRowProps,
} from '@mui/x-data-grid';
import { Decimal } from 'decimal.js';
import { keyBy, pick } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  minValue,
  required,
  SelectInput,
  useDataProvider,
  useRecordContext,
  useTranslate,
} from 'react-admin';
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import {
  GetB2bCampaignAllocationsEndpoint,
  GetB2bCampaignOneResponse,
} from '@Common/types/bo/b2b/campaigns';
import { GetB2bEmployeesEndpoint } from '@Common/types/bo/b2b/employees';
import { MAX_RANGE_ITEMS } from '@Common/types/bo/utils/build_zod_bo_list_query';

import {
  AllocationRecord,
  getBlockedCampaignEmployees,
  getInvalidEmployees,
  getNameMismatches,
  NameMismatch,
} from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/AllocationsConfirmationStep';
import { BlockedCampaignEmployeesSection } from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/components/BlockedCampaignEmployeesSection';
import { InvalidCampaignEmployeesSection } from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/components/InvalidCampaignEmployeesSection';
import { NameMismatchEmployeeSection } from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/components/NameMismatchEmployeeSection';
import { WarningCampaignEmployeesSection } from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/components/WarningCampaignEmployeesSection';
import { pickPreferredEmployeeByIdentifier } from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/pickPreferredEmployeeByIdentifier';
import {
  AMOUNT_FIELD_BY_TYPE,
  DayTypeAllocation,
  getAvailableDayTypes,
  getDaysByType,
  getInvestedDaysFromFile,
  hasDayTypesMismatch,
  INVESTED_DAYS_FIELD_BY_TYPE,
  isZeroAmount,
  VacationDaysAllocationFormRow,
} from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/vacationDaysAllocationRows';
import { AmountInput, CurrencyField } from '@sh4res/bo-common/fields';
import { useResourceBasePath } from '@sh4res/bo-common/hooks';
import { inputDisabledProps } from '@sh4res/bo-common/utils';

const testId = 'BoExternalB2bCampaignVacationDaysAllocationsConfirmationStep';

const rowHeight = 70;

const handleArrowKeyNavigation = (event: React.KeyboardEvent<HTMLDivElement>): void => {
  if (
    event.target instanceof HTMLInputElement &&
    (event.key === 'ArrowLeft' || event.key === 'ArrowRight')
  ) {
    event.stopPropagation();
  }
};

const ZeroAmountWarning = () => {
  const translate = useTranslate();
  return (
    <Tooltip
      title={translate(
        'resources.bo_common.b2b.campaigns.sections.review.errors.zero_amount_allocations',
      )}>
      <WarningIcon
        data-testid={`${testId}_ZeroAmountWarningIcon`}
        color="warning"
        fontSize="small"
      />
    </Tooltip>
  );
};

const DetailPanelContent = ({ rowId }: { rowId: number }) => {
  const { control } = useFormContext();
  const translate = useTranslate();
  const allocation = useWatch({ name: `allocations.${rowId}` }) as
    | VacationDaysAllocationFormRow
    | undefined;
  const dayTypeAllocations = allocation?.day_type_allocations ?? [];

  return (
    <Box>
      {dayTypeAllocations.map((dayTypeAllocation, index) => {
        const amountName = `allocations.${rowId}.day_type_allocations.${index}.amount`;
        const investedDays =
          allocation?.[INVESTED_DAYS_FIELD_BY_TYPE[dayTypeAllocation.days_type]] ?? null;

        return (
          <Paper
            key={dayTypeAllocation.days_type}
            elevation={0}
            sx={{
              height: rowHeight,
              display: 'flex',
              alignItems: 'center',
              columnGap: '4px',
              backgroundColor: index % 2 === 0 ? '#f5f5f5' : 'transparent',
            }}>
            <Box width="50px" />
            <Box flex="4" />
            <Box flex="3" />
            <Box flex="3" />
            <Box flex="3">{investedDays}</Box>
            <Box flex="4" sx={{ overflowX: 'hidden', pb: '16px', pt: '8px' }}>
              <Controller
                name={`allocations.${rowId}.day_type_allocations.${index}.days_type`}
                control={control}
                render={({ field }) => (
                  <SelectInput
                    data-testid={`${testId}_DayTypeSelectInput`}
                    {...field}
                    source={`allocations.${rowId}.day_type_allocations.${index}.days_type`}
                    label={false}
                    helperText={false}
                    fullWidth
                    onKeyDown={handleArrowKeyNavigation}
                    choices={getAvailableDayTypes(allocation ?? {}).map((dayType) => ({
                      id: dayType,
                      name: translate(
                        `resources.bo_common.b2b.campaigns.sections.vacation_days_confirmation.day_types.${dayType}`,
                      ),
                    }))}
                    {...inputDisabledProps(true)}
                  />
                )}
              />
            </Box>
            <Box
              flex="4"
              display="flex"
              alignItems="center"
              sx={{ overflowX: 'auto', pb: '16px', pt: '8px' }}>
              <Controller
                name={amountName}
                control={control}
                render={({ field }) => (
                  <AmountInput
                    data-testid={`${testId}_DayTypeAmountInput`}
                    {...field}
                    source={amountName}
                    label={false}
                    validate={[required(), minValue(0.01)]}
                    min={0}
                    onKeyDown={handleArrowKeyNavigation}
                  />
                )}
              />
              <Typography ml={1}>€</Typography>
              {isZeroAmount(dayTypeAllocation.amount) && <ZeroAmountWarning />}
            </Box>
          </Paper>
        );
      })}
    </Box>
  );
};

const CustomRow = ({
  row,
  expandedRowIds,
  ...props
}: GridRowProps & { expandedRowIds: number[] }) => {
  if (!row) {
    return null;
  }

  return (
    <Box height={props.rowHeight}>
      <GridRow {...props} row={row} rowHeight={rowHeight} />
      {expandedRowIds.includes(row.id) && (
        <Collapse in timeout="auto" unmountOnExit>
          <DetailPanelContent rowId={row.id} />
        </Collapse>
      )}
    </Box>
  );
};

const AmountCell = ({ id }: { id: number | string }) => {
  const amount = useWatch({ name: `allocations.${id}.amount` }) as number | null | undefined;
  const dayTypeAllocations = useWatch({ name: `allocations.${id}.day_type_allocations` }) as
    | DayTypeAllocation[]
    | undefined;
  const hasZeroAmount =
    isZeroAmount(amount) || dayTypeAllocations?.some((entry) => isZeroAmount(entry.amount));
  return (
    <Box display="flex" alignItems="center" columnGap={1}>
      {G.isNotNullable(amount) && (
        <CurrencyField
          data-testid={`${testId}_AllocationAmountField`}
          source="amount"
          currency="eur"
          record={{ amount }}
        />
      )}
      {hasZeroAmount && <ZeroAmountWarning />}
    </Box>
  );
};

export const VacationDaysAllocationsConfirmationStep = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [expandedRowIds, setExpandedRowIds] = useState<number[]>([]);
  const [nameMismatches, setNameMismatches] = useState<NameMismatch[]>([]);
  const { control, setValue } = useFormContext();
  const record = useRecordContext<GetB2bCampaignOneResponse>();
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const resourceBasePath = useResourceBasePath();

  const { fields } = useFieldArray({ control, name: 'allocations' });

  const [companyId, allocations, contractIds] = useWatch({
    name: ['company_id', 'allocations', 'b2b_contract_ids', 'type'],
  });

  const invalidEmployees = useMemo(
    () => getInvalidEmployees((allocations ?? []) as AllocationRecord[]),
    [allocations],
  );

  const { regulatory: blockedCampaignEmployees, restriction: restrictionBlockedEmployees } =
    useMemo(
      () => getBlockedCampaignEmployees((allocations ?? []) as AllocationRecord[]),
      [allocations],
    );

  const warningEmployees = useMemo(
    () =>
      restrictionBlockedEmployees.map((employee) => ({
        ...employee,
        warning: 'campaigns_blocked_restriction',
      })),
    [restrictionBlockedEmployees],
  );

  useEffect(() => {
    const fetchAllRecords = async () => {
      if (
        allocations?.length &&
        allocations.every((alloc: VacationDaysAllocationFormRow) =>
          G.isNotNullable(alloc.employee_exists),
        )
      ) {
        return;
      }

      setIsLoading(true);
      try {
        const [{ data: employeesData }, { data: existingAllocations }] = await Promise.all([
          dataProvider.getList<GetB2bEmployeesEndpoint['Client']['ResponseBody'][number]>(
            `${resourceBasePath}employees`,
            {
              filter: { company_id: companyId, contract_ids: contractIds },
              sort: { field: 'created_at', order: 'DESC' },
              pagination: { page: 1, perPage: MAX_RANGE_ITEMS },
            },
          ),
          G.isNotNullable(record?.id)
            ? dataProvider.getList<
                GetB2bCampaignAllocationsEndpoint['Client']['ResponseBody'][number]
              >(`${resourceBasePath}campaigns/allocations`, {
                filter: { campaign_id: record.id },
                sort: { field: 'employee_id', order: 'ASC' },
                pagination: { page: 1, perPage: MAX_RANGE_ITEMS },
              })
            : Promise.resolve({
                data: [] as GetB2bCampaignAllocationsEndpoint['Client']['ResponseBody'],
              }),
        ]);

        // When there are multiple employees with the same NIR/NTT, prefer the one with status "current"
        const preferredEmployees = pickPreferredEmployeeByIdentifier(employeesData);
        const employeeByIdentifier: Record<
          string,
          GetB2bEmployeesEndpoint['Client']['ResponseBody'][number] | undefined
        > = Object.fromEntries(preferredEmployees);
        const allocationByEmployeeId: Record<
          number,
          GetB2bCampaignAllocationsEndpoint['Client']['ResponseBody'][number] | undefined
        > = keyBy(existingAllocations, 'employee_id');

        const toFormRow = (
          employee: GetB2bEmployeesEndpoint['Client']['ResponseBody'][number] | undefined,
          existingAllocation:
            | GetB2bCampaignAllocationsEndpoint['Client']['ResponseBody'][number]
            | undefined,
          fileDayTypeAllocations: DayTypeAllocation[] = [],
        ) => {
          // The uploaded file, if any, overrides the saved allocation for days and amounts
          const isFromFile = fileDayTypeAllocations.length > 0;
          // Days by type are the days invested, which the per-type amounts are entered against.
          const savedInvestedDays = pick(
            existingAllocation,
            Object.values(INVESTED_DAYS_FIELD_BY_TYPE),
          );
          // The backend validates the amounts against the day types saved on the employee's
          // allocation: they are all listed, whatever the file contains
          const savedDayTypes = getAvailableDayTypes(savedInvestedDays);
          const fileInvestedDays = getInvestedDaysFromFile(fileDayTypeAllocations);
          const getInvestedDays = () => {
            if (!isFromFile) {
              return savedInvestedDays;
            }
            if (!existingAllocation) {
              return fileInvestedDays;
            }
            return {
              ...savedInvestedDays,
              ...pick(
                fileInvestedDays,
                savedDayTypes.map((dayType) => INVESTED_DAYS_FIELD_BY_TYPE[dayType]),
              ),
            };
          };
          const investedDays = getInvestedDays();
          const dayTypes = getAvailableDayTypes(investedDays);
          return {
            ...investedDays,
            ...getDaysByType(investedDays),
            day_types_mismatch:
              isFromFile &&
              G.isNotNullable(existingAllocation) &&
              hasDayTypesMismatch(
                fileDayTypeAllocations.map((entry) => entry.days_type),
                savedDayTypes,
              ),
            day_type_allocations: dayTypes.map((dayType) => ({
              days_type: dayType,
              amount: isFromFile
                ? (fileDayTypeAllocations.find((entry) => entry.days_type === dayType)?.amount ??
                  null)
                : (existingAllocation?.[AMOUNT_FIELD_BY_TYPE[dayType]] ?? null),
            })),
            ...pick(
              employee,
              'nir',
              'ntt',
              'firstname',
              'lastname',
              'registration_number',
              'social_status',
              'investor_profile',
              'pei_investor_profile',
              'tax_residence_code',
              'is_campaigns_blocked',
              'is_campaigns_blocked_regulatory',
              'is_user_closed_or_archived',
            ),
            employee_id: employee?.id,
            employee_exists: G.isNotNullable(employee),
            employee_fully_onboarded: employee?.approbation_state === 'finished',
            contract_ids: employee?.contract_ids ?? [],
            invested_amount_in_days: existingAllocation?.invested_amount_in_days ?? null,
          };
        };

        if (!allocations?.length) {
          // No file uploaded: display the campaign's existing allocations
          const employeeById = keyBy(employeesData, 'id');
          setValue(
            'allocations',
            existingAllocations.map((existingAllocation) => {
              const employee = employeeById[existingAllocation.employee_id];
              return {
                ...toFormRow(employee, existingAllocation),
                identifier: existingAllocation.identifier,
                amount: existingAllocation.net_amount,
              };
            }),
          );
        } else {
          setNameMismatches(getNameMismatches(allocations, employeeByIdentifier));
          const employeeById = keyBy(employeesData, 'id');
          const fileEmployeeIds = new Set<number>();
          const fileRows = A.map(allocations as VacationDaysAllocationFormRow[], (alloc) => {
            const identifier = (alloc.nir ?? alloc.ntt) as string;
            const employee = employeeByIdentifier[identifier];
            if (employee) {
              fileEmployeeIds.add(employee.id);
            }
            const existingAllocation = employee ? allocationByEmployeeId[employee.id] : undefined;
            return {
              ...alloc,
              ...toFormRow(employee, existingAllocation, alloc.day_type_allocations),
              identifier,
            };
          });
          // Employees missing from the file stay listed, with a zero amount on every day type
          const missingRows = existingAllocations
            .filter((existingAllocation) => !fileEmployeeIds.has(existingAllocation.employee_id))
            .map((existingAllocation) => {
              const row = toFormRow(
                employeeById[existingAllocation.employee_id],
                existingAllocation,
              );
              return {
                ...row,
                day_type_allocations: row.day_type_allocations.map((entry) => ({
                  ...entry,
                  amount: 0,
                })),
                identifier: existingAllocation.identifier,
                amount: 0,
              };
            });
          setValue('allocations', [...fileRows, ...missingRows], { shouldDirty: true });
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllRecords();
    // Intentionally re-fetches only when the company/contract selection changes.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, contractIds]);

  const totalNetAmount = useMemo(
    () =>
      ((allocations ?? []) as VacationDaysAllocationFormRow[])
        .reduce((total, alloc) => total.plus(alloc.amount ?? 0), new Decimal(0))
        .toNumber(),
    [allocations],
  );

  // The row amount is the sum of the amounts entered by day type
  useEffect(() => {
    ((allocations ?? []) as VacationDaysAllocationFormRow[]).forEach((alloc, index) => {
      const dayTypeAllocations = alloc.day_type_allocations ?? [];
      if (dayTypeAllocations.length === 0) {
        return;
      }
      const sum = dayTypeAllocations
        .reduce((total, entry) => total.plus(entry.amount ?? 0), new Decimal(0))
        .toNumber();
      if (sum !== (alloc.amount ?? 0)) {
        setValue(`allocations.${index}.amount`, sum, { shouldDirty: true });
      }
    });
  }, [allocations, setValue]);

  const toggleRow = useCallback(
    (id: number) =>
      setExpandedRowIds((previous) =>
        previous.includes(id) ? previous.filter((rowId) => rowId !== id) : [...previous, id],
      ),
    [],
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: '_expand',
        headerName: '',
        sortable: false,
        width: 50,
        renderCell: (params: GridRenderCellParams) => (
          <IconButton
            data-testid={`${testId}_ExpandRowButton`}
            size="small"
            onClick={() => toggleRow(params.row.id)}
            aria-label={translate('resources.bo_common.accessibility.toggle')}>
            {expandedRowIds.includes(params.row.id) ? (
              <KeyboardArrowUpIcon />
            ) : (
              <KeyboardArrowDownIcon />
            )}
          </IconButton>
        ),
      },
      {
        field: 'identifier',
        headerName: translate('resources.bo_common.b2b.campaigns.sections.allocations.identifier'),
        flex: 4,
      },
      {
        field: 'firstname',
        headerName: translate('resources.bo_common.b2b.employees.fields.firstname'),
        flex: 3,
      },
      {
        field: 'lastname',
        headerName: translate('resources.bo_common.b2b.employees.fields.lastname'),
        flex: 3,
      },
      {
        field: 'invested_amount_in_days',
        headerName: translate(
          'resources.bo_common.b2b.campaigns.sections.vacation_days_confirmation.invested_amount_in_days',
        ),
        flex: 3,
        sortable: false,
      },
      {
        field: '_day_type',
        headerName: translate(
          'resources.bo_common.b2b.campaigns.sections.vacation_days_confirmation.day_type',
        ),
        flex: 4,
        sortable: false,
      },
      {
        field: '_amount',
        headerName: translate(
          'resources.bo_common.b2b.campaigns.sections.vacation_days_confirmation.net_amount_column',
        ),
        flex: 4,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => <AmountCell id={params.id} />,
      },
    ],
    [translate, expandedRowIds, toggleRow],
  );

  return (
    <Box height="100%" display="flex" flexDirection="column">
      <Typography>
        {translate(
          'resources.bo_common.b2b.campaigns.sections.vacation_days_confirmation.total_net_amount',
        )}
        :{' '}
        <CurrencyField
          data-testid={`${testId}_TotalNetAmountField`}
          source="amount"
          currency="eur"
          record={{ amount: totalNetAmount }}
        />
      </Typography>
      <DataGrid
        rows={fields.map((field, index) => ({ ...field, id: index }))}
        columns={columns}
        disableSelectionOnClick
        initialState={{ pagination: { pageSize: 10 } }}
        components={{ Row: CustomRow }}
        componentsProps={{ row: { expandedRowIds } }}
        loading={isLoading}
        getRowHeight={(params) => {
          if (!expandedRowIds.includes(params.id as number)) {
            return rowHeight;
          }
          const nDayTypes = (
            allocations?.[params.id as number] as VacationDaysAllocationFormRow | undefined
          )?.day_type_allocations?.length;
          return ((nDayTypes ?? 0) + 1) * rowHeight;
        }}
        rowsPerPageOptions={[10, 20, 50]}
        disableColumnMenu
        sx={{
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
            outline: 'none',
          },
          '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
            outline: 'none',
          },
          '& .MuiTablePagination-root .MuiToolbar-root': {
            paddingLeft: 0,
            paddingRight: 0,
          },
        }}
      />
      <InvalidCampaignEmployeesSection invalidEmployees={invalidEmployees} />
      <WarningCampaignEmployeesSection warningEmployees={warningEmployees} />
      <BlockedCampaignEmployeesSection blockedCampaignEmployees={blockedCampaignEmployees} />
      <NameMismatchEmployeeSection nameMismatches={nameMismatches} />
    </Box>
  );
};
